import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { solutionKeys } from "@/lib/query";

export type ItemType =
  | "feature"
  | "implied"
  | "tech-req"
  | "tech-cons"
  | "business"
  | "integration"
  | "ui"
  | "copy"
  | "process"
  | "nfr"
  | "passing";
export type Confidence = "low" | "medium" | "high";
export type TriageStatus = "pending" | "in-scope" | "in-review" | "out-of-scope" | "deferred";

export interface AcceptanceCriterion {
  id: string;
  storyId: string;
  text: string;
  sortOrder: number;
  createdAt: string;
}

export interface BaselineStory {
  id: string;
  epicId: string;
  code: string;
  label: string | null;
  text: string;
  sortOrder: number;
  createdAt: string;
  acceptanceCriteria: AcceptanceCriterion[];
}

export interface BaselineEpic {
  id: string;
  deliverableId: string;
  code: string;
  title: string;
  sortOrder: number;
  createdAt: string;
  stories: BaselineStory[];
}

export interface BaselineDeliverable {
  id: string;
  solutionId: string;
  code: string;
  title: string;
  group: string;
  summary: string | null;
  sortOrder: number;
  createdAt: string;
  epics: BaselineEpic[];
}

export interface MatchedItem {
  id: string;
  externalKey: string | null;
  type: ItemType;
  extracted: string;
  overlapPct: number;
  confidence: Confidence;
  suggestedStatus: TriageStatus;
  effectiveStatus: TriageStatus;
}

export interface StoryDetail extends BaselineStory {
  epicCode: string | null;
  epicTitle: string | null;
  deliverableCode: string | null;
  deliverableTitle: string | null;
  solutionId: string | null;
  matchedItems: MatchedItem[];
}

export const baselineKeys = {
  tree: (solutionId: string) => ["baseline", "tree", solutionId] as const,
  story: (storyId: string) => ["baseline", "story", storyId] as const,
} as const;

export function useBaseline(solutionId: string) {
  return useQuery({
    queryKey: baselineKeys.tree(solutionId),
    queryFn: async (): Promise<BaselineDeliverable[]> => {
      const res = await fetch(`/api/solutions/${solutionId}/baseline`);
      if (!res.ok) throw new Error("Failed to fetch baseline");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(solutionId),
  });
}

export function useConfirmBaseline(solutionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ baselineConfirmedAt: string }> => {
      const res = await fetch(`/api/solutions/${solutionId}/baseline/confirm`, {
        method: "POST",
      });
      if (!res.ok) {
        const json = await res.json().catch(() => null);
        throw new Error(json?.error?.message ?? "Failed to confirm baseline");
      }
      const json = await res.json();
      return json.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: solutionKeys.detail(solutionId) });
      toast.success("Baseline confirmed — Process AI is now enabled");
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useStory(storyId: string | null) {
  return useQuery({
    queryKey: baselineKeys.story(storyId ?? ""),
    queryFn: async (): Promise<StoryDetail> => {
      const res = await fetch(`/api/stories/${storyId}`);
      if (!res.ok) throw new Error("Failed to fetch story");
      const json = await res.json();
      return json.data;
    },
    enabled: Boolean(storyId),
  });
}
